import { useEffect, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChartBar, Download, Table as TableIcon, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { UNIT_STATUS, TENANCY_STATUS } from "@/lib/constants";

const eur = (v) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const pct = (v) => (v >= 0 ? "+" : "") + Number(v || 0).toFixed(1) + " %";

export default function Analytics() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/analytics/overview", { params: { year } }).then(({ data }) => setOverview(data));
  }, [year]);

  const dl = async (type, fmt) => {
    try {
      const r = await fetch(`${API}/analytics/export?type=${type}&format=${fmt}&year=${year}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error("Export fehlgeschlagen");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Domivus_${type}_${year}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export bereit");
    } catch (e) { toast.error(e.message); }
  };

  const years = [];
  for (let y = thisYear; y >= thisYear - 4; y--) years.push(y);

  return (
    <div className="space-y-4" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><ChartBar size={22} /> Auswertungen</h1>
          <p className="text-sm text-slate-500 mt-1">KPIs, Jahresvergleich, Detailberichte mit Export.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="analytics-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="analytics-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="by-property" data-testid="analytics-tab-byprop">Je Objekt</TabsTrigger>
          <TabsTrigger value="by-unit" data-testid="analytics-tab-byunit">Je Einheit</TabsTrigger>
          <TabsTrigger value="by-tenancy" data-testid="analytics-tab-bymv">Je Mietverhältnis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4 space-y-4">
          <OverviewView data={overview} />
        </TabsContent>

        <TabsContent value="by-property" className="pt-4">
          <DetailTable kind="by-property" year={year} onExport={dl} />
        </TabsContent>
        <TabsContent value="by-unit" className="pt-4">
          <DetailTable kind="by-unit" year={year} onExport={dl} />
        </TabsContent>
        <TabsContent value="by-tenancy" className="pt-4">
          <DetailTable kind="by-tenancy" year={year} onExport={dl} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewView({ data }) {
  if (!data) return <div className="text-slate-400 text-sm">Lade…</div>;
  const yoyIst = data.yoy.ist;
  const yoySoll = data.yoy.soll;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Objekte" value={data.kpi.properties} />
        <Kpi label="Einheiten" value={`${data.kpi.units_total}`} sub={`${data.kpi.vacant_units} leer`} />
        <Kpi label="Auslastung" value={`${data.kpi.occupancy_rate} %`} />
        <Kpi label="Aktive Mietverhältnisse" value={data.kpi.active_tenancies} />
        <Kpi label="Mieteinnahmen (Ist)" value={eur(yoyIst.current)} sub={pct(yoyIst.pct) + " ggü. Vorjahr"} positive={yoyIst.pct >= 0} />
        <Kpi label="Rückstand" value={eur(data.kpi.outstanding)} negative />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm">
          <div className="text-sm font-semibold mb-2">Soll vs. Vorjahr</div>
          <RowYoy label="Soll" cur={yoySoll.current} prev={yoySoll.previous} pct={yoySoll.pct} />
          <RowYoy label="Ist" cur={yoyIst.current} prev={yoyIst.previous} pct={yoyIst.pct} />
          <div className="flex justify-between text-xs text-slate-500 mt-3">
            <div>Ist-/Soll-Quote: <span className="font-semibold tabular-nums text-slate-800">{data.yoy.ratio_current} %</span></div>
            <div>Vorjahr: <span className="font-mono text-slate-500">{data.yoy.ratio_previous} %</span></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm">
          <div className="text-sm font-semibold mb-2">Mieterscore-Verteilung</div>
          {data.score.evaluated > 0 ? (
            <div className="space-y-1.5">
              {["A","B","C","D","E"].map((g) => {
                const n = data.score.buckets[g] || 0;
                const max = Math.max(...Object.values(data.score.buckets), 1);
                const colors = { A: "bg-emerald-600", B: "bg-lime-500", C: "bg-amber-500", D: "bg-orange-600", E: "bg-rose-700" };
                return (
                  <div key={g} className="flex items-center gap-2">
                    <div className={`w-6 h-6 ${colors[g]} text-white text-xs flex items-center justify-center font-bold rounded-sm`}>{g}</div>
                    <div className="flex-1 bg-slate-100 rounded-sm h-2 overflow-hidden"><div className={`h-full ${colors[g]}`} style={{ width: `${(n / max) * 100}%` }} /></div>
                    <div className="text-xs tabular-nums w-8 text-right">{n}</div>
                  </div>
                );
              })}
              <div className="text-xs text-slate-500 pt-1">⌀ Score: <strong>{data.score.average ?? "—"}</strong> · {data.score.evaluated} bewertet</div>
            </div>
          ) : <div className="text-xs text-slate-400">Mieterscore deaktiviert</div>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm">
        <div className="text-sm font-semibold mb-2">Monatlicher Ertrag · Ist (aktuell vs. Vorjahr)</div>
        <MonthBars current={data.months_current} previous={data.months_previous} />
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, positive, negative }) {
  const color = positive ? "text-emerald-700" : negative ? "text-rose-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-semibold tabular-nums mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RowYoy({ label, cur, prev, pct }) {
  const up = pct >= 0;
  return (
    <div className="flex items-baseline justify-between border-t border-slate-100 py-2">
      <div className="text-sm text-slate-700">{label}</div>
      <div className="text-right">
        <div className="text-base tabular-nums font-semibold text-slate-900">{eur(cur)}</div>
        <div className="text-xs text-slate-500">Vorjahr {eur(prev)} <span className={up ? "text-emerald-700" : "text-rose-700"}>· {pct >= 0 ? "+" : ""}{pct} %</span></div>
      </div>
    </div>
  );
}

function MonthBars({ current, previous }) {
  const prevByMonth = {};
  previous.forEach((m) => { prevByMonth[m.month.slice(5)] = m.ist; });
  const max = Math.max(1, ...current.map((m) => m.ist), ...previous.map((m) => m.ist));
  if (!current.length && !previous.length) return <div className="text-xs text-slate-400">Keine Daten</div>;
  return (
    <div className="flex items-end gap-1 h-32" data-testid="month-bars">
      {current.map((m) => {
        const mm = m.month.slice(5);
        const pv = prevByMonth[mm] || 0;
        return (
          <div key={m.month} className="flex-1 flex flex-col items-center">
            <div className="flex items-end gap-0.5 h-28 w-full">
              <div className="flex-1 bg-emerald-500 rounded-sm" style={{ height: `${(m.ist / max) * 100}%` }} title={`Akt: ${eur(m.ist)}`} />
              <div className="flex-1 bg-slate-300 rounded-sm" style={{ height: `${(pv / max) * 100}%` }} title={`Vorj: ${eur(pv)}`} />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{mm}</div>
          </div>
        );
      })}
    </div>
  );
}

function DetailTable({ kind, year, onExport }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/analytics/${kind}`, { params: { year } }).then(({ data }) => setData(data));
  }, [kind, year]);
  if (!data) return <div className="text-slate-400 text-sm">Lade…</div>;
  const cols = COL_DEFS[kind];
  return (
    <div className="space-y-2">
      <div className="flex justify-end gap-2">
        <Button variant="outline" className="gap-2" onClick={() => onExport(kind, "csv")} data-testid={`export-csv-${kind}`}>
          <Download size={14} /> CSV
        </Button>
        <Button variant="outline" className="gap-2" onClick={() => onExport(kind, "xlsx")} data-testid={`export-xlsx-${kind}`}>
          <FileXls size={14} /> XLSX
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>{cols.map((c) => (
              <th key={c.k} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 ${c.right ? "text-right" : ""}`}>{c.l}</th>
            ))}</tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={cols.length} className="p-6 text-center text-slate-400">Keine Daten</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                {cols.map((c) => (
                  <td key={c.k} className={`px-3 py-2 ${c.right ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono text-xs" : ""}`}>
                    {c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const COL_DEFS = {
  "by-property": [
    { k: "property_number", l: "Objekt", mono: true },
    { k: "name", l: "Name" }, { k: "city", l: "Stadt" },
    { k: "units", l: "Einh.", right: true },
    { k: "vacant", l: "Leer", right: true, fmt: (v) => v > 0 ? <span className="text-amber-700">{v}</span> : v },
    { k: "occupancy_rate", l: "Auslast. %", right: true, fmt: (v) => v + " %" },
    { k: "ist_current", l: "Ist aktuell", right: true, fmt: eur },
    { k: "ist_previous", l: "Ist Vorjahr", right: true, fmt: (v) => <span className="text-slate-400">{eur(v)}</span> },
    { k: "yoy_ist_pct", l: "YoY", right: true, fmt: (v) => <span className={v >= 0 ? "text-emerald-700" : "text-rose-700"}>{pct(v)}</span> },
    { k: "outstanding", l: "Rückstand", right: true, fmt: (v) => v > 0 ? <span className="text-rose-700">{eur(v)}</span> : eur(v) },
  ],
  "by-unit": [
    { k: "property_number", l: "Objekt", mono: true },
    { k: "unit_number", l: "Einheit", mono: true },
    { k: "living_area", l: "Wfl.", right: true, fmt: (v) => v ? v + " m²" : "—" },
    { k: "status", l: "Status", fmt: (v) => UNIT_STATUS[v]?.label || v || "—" },
    { k: "ist_current", l: "Ist aktuell", right: true, fmt: eur },
    { k: "ist_previous", l: "Ist Vorjahr", right: true, fmt: (v) => <span className="text-slate-400">{eur(v)}</span> },
    { k: "yoy_ist_pct", l: "YoY", right: true, fmt: (v) => <span className={v >= 0 ? "text-emerald-700" : "text-rose-700"}>{pct(v)}</span> },
    { k: "outstanding", l: "Rückstand", right: true, fmt: (v) => v > 0 ? <span className="text-rose-700">{eur(v)}</span> : eur(v) },
  ],
  "by-tenancy": [
    { k: "contract_number", l: "Vertrag", mono: true },
    { k: "tenant_name", l: "Mieter" }, { k: "status", l: "Status", fmt: (v) => TENANCY_STATUS[v]?.label || v || "—" },
    { k: "rent_cold", l: "Kaltm.", right: true, fmt: eur },
    { k: "soll_current", l: "Soll aktuell", right: true, fmt: eur },
    { k: "ist_current", l: "Ist aktuell", right: true, fmt: eur },
    { k: "saldo", l: "Saldo", right: true, fmt: (v) => <span className={v < 0 ? "text-rose-700" : "text-emerald-700"}>{eur(v)}</span> },
    { k: "yoy_ist_pct", l: "YoY", right: true, fmt: (v) => <span className={v >= 0 ? "text-emerald-700" : "text-rose-700"}>{pct(v)}</span> },
  ],
};
